/**
 * 문제집 목록 테이블 컴포넌트
 * - 문제집 목록 표시
 * - 역할별 액션 메뉴
 * - 페이지네이션
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Dropdown,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  EyeOutlined,
  UserAddOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { workbookService } from '../../services/workbookService';
import type { Workbook, WorkbookListParams } from '../../types/workbook';
import type { WorkbookFilterState } from './WorkbookFilters';
import { useAuthStore } from '../../store/authStore';

interface WorkbookTableProps {
  filters: WorkbookFilterState;
  onAssign: (workbook: Workbook) => void;
  refreshTrigger?: number;
}

/**
 * WorkbookTable 컴포넌트
 * 문제집 목록 테이블
 */
export const WorkbookTable: React.FC<WorkbookTableProps> = ({
  filters,
  onAssign,
  refreshTrigger,
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [workbooks, setWorkbooks] = useState<Workbook[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  /**
   * 문제집 목록 조회
   */
  const fetchWorkbooks = async () => {
    setLoading(true);
    try {
      const params: WorkbookListParams = {
        page,
        page_size: pageSize,
        ...filters,
      };

      const response = await workbookService.getWorkbooks(params);
      setWorkbooks(response.workbooks);
      setTotal(response.total);
    } catch (error: any) {
      message.error('문제집 목록 조회 실패');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드 및 필터/페이지 변경 시 재조회
   */
  useEffect(() => {
    fetchWorkbooks();
  }, [page, pageSize, filters, refreshTrigger]);

  /**
   * 페이지 변경 처리
   */
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 20);
  };

  /**
   * 문제집 상세 페이지로 이동
   */
  const handleViewDetail = (workbook: Workbook) => {
    navigate(`/workbooks/${encodeURIComponent(workbook.name)}`);
  };

  /**
   * 문제집 삭제
   */
  const handleDelete = async (workbook: Workbook) => {
    const confirmed = window.confirm(
      `"${workbook.name}" 문제집을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await workbookService.deleteWorkbook(workbook.name);
      message.success('문제집이 삭제되었습니다');
      fetchWorkbooks(); // 목록 새로고침
    } catch (error: any) {
      console.error('Delete error:', error);
      message.error(`문제집 삭제 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  /**
   * 액션 메뉴 아이템 생성
   */
  const getActionMenuItems = (record: Workbook): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: '상세보기',
        onClick: () => handleViewDetail(record),
      },
    ];

    // instructor, group_admin: 배정 메뉴 추가
    if (currentUser?.role === 'instructor' || currentUser?.role === 'group_admin') {
      items.push({
        key: 'assign',
        icon: <UserAddOutlined />,
        label: '학생 배정',
        onClick: () => onAssign(record),
      });
    }

    // admin만 삭제 가능
    if (currentUser?.role === 'admin') {
      items.push(
        {
          type: 'divider',
        },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: '삭제',
          danger: true,
          onClick: () => handleDelete(record),
        }
      );
    }

    return items;
  };

  /**
   * 학년 표시 (1~12 → 초1~고3)
   */
  const getGradeLabel = (grade: number): string => {
    if (grade >= 1 && grade <= 6) return `초${grade}`;
    if (grade >= 7 && grade <= 9) return `중${grade - 6}`;
    if (grade >= 10 && grade <= 12) return `고${grade - 9}`;
    return `${grade}`;
  };

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<Workbook> = [
    {
      title: '문제집명',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (name) => (
        <Button type="link" onClick={() => handleViewDetail({ name } as Workbook)}>
          {name}
        </Button>
      ),
    },
    {
      title: '연도',
      dataIndex: 'year',
      key: 'year',
      width: 100,
      render: (year) => `${year}년`,
    },
    {
      title: '학년',
      dataIndex: 'grade',
      key: 'grade',
      width: 100,
      render: (grade) => <Tag color="blue">{getGradeLabel(grade)}</Tag>,
    },
    {
      title: '학기',
      dataIndex: 'semester',
      key: 'semester',
      width: 100,
      render: (semester) => (semester ? `${semester}학기` : '-'),
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (date ? new Date(date).toLocaleDateString('ko-KR') : '-'),
    },
    {
      title: '액션',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={workbooks}
      rowKey="name"
      loading={loading}
      pagination={{
        current: page,
        pageSize: pageSize,
        total: total,
        showSizeChanger: true,
        showTotal: (total) => `총 ${total}개`,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      onChange={handleTableChange}
      scroll={{ x: 900 }}
    />
  );
};
